/**
 * Unified HTTP Client for API communication
 * Provides consistent headers, error handling, and request/response processing
 * Connects to the enhanced backend APIs created in Phase 2
 */
import axios from 'axios';

// Resolve API base URL with sensible defaults:
// 1) Use REACT_APP_API_BASE_URL if provided at build time
// 2) If running locally, default to http://localhost:8000
// 3) In production, use same-origin (empty baseURL) so '/api/*' hits CloudFront route
const resolveApiBaseURL = () => {
    const hasProcessEnv = typeof process !== 'undefined' && process.env && Object.prototype.hasOwnProperty.call(process.env, 'REACT_APP_API_BASE_URL');
    const envUrl = hasProcessEnv && process.env.REACT_APP_API_BASE_URL ? String(process.env.REACT_APP_API_BASE_URL).trim() : '';
    if (envUrl) return envUrl;

    if (typeof window !== 'undefined') {
        const host = window.location.hostname;
        const isLocal = host === 'localhost' || host === '127.0.0.1';
        return isLocal ? 'http://localhost:8000' : '';
    }
    // Fallback for non-browser contexts
    return '';
};

const apiBaseURL = resolveApiBaseURL();
const httpClient = axios.create({
    baseURL: apiBaseURL,
    timeout: 300000, // 30 seconds timeout
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Debug logging (safe)
console.log('[HttpClient] Created with baseURL:', httpClient.defaults.baseURL || '(same-origin)');
try {
    if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BASE_URL) {
        console.log('[HttpClient] Environment API URL set');
    }
} catch (_) { /* ignore */ }

// Public paths that don't require authentication
const PUBLIC_PATHS = [
    '/api/accounts/login/',
    '/api/accounts/register/',
    '/api/accounts/verify-email/',
    '/api/accounts/captcha-config/',
    '/api/accounts/verify-code/',
    '/api/accounts/check-verify-code/',
    '/api/accounts/token/refresh/',
    '/api/accounts/invitation-code/',
];

// Check if path is public (no auth required)
const isPublicPath = (url) => {
    return PUBLIC_PATHS.some(publicPath => url.includes(publicPath));
};

// Decode JWT token payload without verification
const decodeToken = (token) => {
    try {
        if (!token) return null;
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const payload = JSON.parse(atob(parts[1]));
        return payload;
    } catch (e) {
        console.warn('[HttpClient] Failed to decode token:', e);
        return null;
    }
};

// Check if token is expired or will expire soon (within 5 minutes)
const isTokenExpiredOrExpiring = (token, bufferSeconds = 300) => {
    const payload = decodeToken(token);
    if (!payload || !payload.exp) return true;
    const now = Math.floor(Date.now() / 1000);
    return payload.exp <= (now + bufferSeconds);
};

// Get user information from localStorage
const getUserInfo = () => {
    try {
        // Prefer unified key 'user', fallback to 'userInfo' for backward compatibility
        const raw = localStorage.getItem('user') || localStorage.getItem('userInfo') || '{}';
        const userInfo = JSON.parse(raw);
        const userId = userInfo.id ?? userInfo.user_id ?? null;
        const orgCode = userInfo.org_code ?? userInfo.organization_code ?? null;
        // Preserve 0 as a valid level; fall back to 0, not 1
        const userLevel = (userInfo.user_level ?? userInfo.level ?? 0);
        return { userId, orgCode, userLevel };
    } catch (error) {
        console.warn('Failed to parse user info from localStorage:', error);
        return {
            userId: null,
            orgCode: null,
            userLevel: 0
        };
    }
};

// Request interceptor to add authentication headers and user context
httpClient.interceptors.request.use(
    async (config) => {
        console.log('[HttpClient] Making request to:', config.baseURL + config.url);

        const { userId, orgCode, userLevel } = getUserInfo();

        // Add user context headers for backend enhanced error handling
        if (userId !== undefined && userId !== null) {
            config.headers['X-User-Id'] = userId;
        }
        if (orgCode !== undefined && orgCode !== null) {
            config.headers['X-Org-Code'] = orgCode;
        }
        // Always send user level, even when it is 0
        if (userLevel !== undefined && userLevel !== null) {
            config.headers['X-User-Level'] = userLevel;
        }

        // Add authorization header if available and not a public path.
        // An expired token is REFRESHED here, not dropped. Dropping it was survivable while
        // the server trusted the X-User-Id header that stayed behind; the JWT bridge now
        // strips unbacked identity headers, so a dropped token is a guaranteed 401.
        const requestUrl = config.url || '';
        if (!isPublicPath(requestUrl)) {
            const token = await ensureFreshToken();
            if (token) {
                config.headers['Authorization'] = `Bearer ${token}`;
            }
        }

        // Add preferred language header for backend to localize messages if supported
        const lang = localStorage.getItem('i18n_lang') || 'en';
        config.headers['Accept-Language'] = lang;

        return config;
    },
    (error) => {
        console.error('[HttpClient] Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor for consistent error handling with auto-refresh support
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// A session is either usable or it is over. Clearing the tokens but leaving 'user' behind
// left the UI believing it was logged in while every request went out unauthenticated —
// harmless while the server trusted X-User-Id, a wall of "server error" pages once it
// stopped. Mirrors the complete logout in components/Agent.js.
const clearSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('user');
    if (typeof window !== 'undefined' && window.location && window.location.pathname !== '/login') {
        window.location.href = '/login';
    }
};

// Return a token that is valid right now, refreshing it first if it has expired.
// Returns null when the session cannot be renewed (caller sends no Authorization header).
const ensureFreshToken = async () => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (token && !isTokenExpiredOrExpiring(token, 0)) {
        return token;
    }

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
        // Nothing can authenticate this request. If the app is still carrying an identity —
        // an expired token, or a leftover 'user' whose tokens were cleared by an earlier
        // partial logout — that identity is worthless, and keeping it only produces
        // unauthenticated requests the user sees as "server error" pages. End the session
        // instead. A visitor with no identity at all is left alone.
        if (token || getUserInfo().userId != null) {
            clearSession();
        }
        return null;
    }

    if (isRefreshing) {
        // Another request is already refreshing; wait for its result instead of racing it.
        return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
        }).catch(() => null);
    }

    isRefreshing = true;
    try {
        const data = await authAPI.refresh(refreshToken);
        const newToken = data && data.success && data.data && data.data.access_token;
        if (!newToken) {
            throw new Error('Token refresh failed');
        }
        localStorage.setItem('token', newToken);
        localStorage.setItem('authToken', newToken);
        localStorage.setItem('refresh_token', data.data.refresh_token || refreshToken);
        processQueue(null, newToken);
        return newToken;
    } catch (err) {
        console.warn('[HttpClient] Token refresh failed:', err);
        processQueue(err, null);
        clearSession();
        return null;
    } finally {
        isRefreshing = false;
    }
};

httpClient.interceptors.response.use(
    (response) => {
        // Extract request ID from response headers for error tracking
        const requestId = response.headers['x-request-id'];
        if (requestId && response.data) {
            response.data._requestId = requestId;
        }
        
        return response;
    },
    (error) => {
        console.error('[HttpClient] Response interceptor error:', error);
        console.error('[HttpClient] Error details:', {
            message: error.message,
            code: error.code,
            response: error.response,
            request: error.request,
            config: error.config
        });
        
        // Enhanced error handling for backend API errors
        if (error.response) {
            const { status, data } = error.response;
            const requestId = error.response.headers['x-request-id'];
            console.log('[HttpClient] HTTP error response:', status, data);
            
            // Auto refresh on 401 with refresh_token available
            if (status === 401) {
                const originalRequest = error.config;

                // prevent infinite loop
                if (originalRequest._retry) {
                    // already retried once; reject
                } else if ((originalRequest.url || '').includes('/api/accounts/token/refresh/')) {
                    // The refresh call itself was rejected: the refresh token is dead. Its
                    // caller holds isRefreshing, so queueing this would wait on itself forever
                    // and strand every request queued behind it. Reject; the caller ends the
                    // session.
                } else {
                    originalRequest._retry = true;
                    const refreshToken = localStorage.getItem('refresh_token');
                    if (refreshToken) {
                        if (isRefreshing) {
                            // queue requests while refreshing
                            return new Promise(function(resolve, reject) {
                                failedQueue.push({ resolve, reject });
                            }).then((newToken) => {
                                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                                return httpClient(originalRequest);
                            }).catch((err) => {
                                return Promise.reject(err);
                            });
                        }

                        isRefreshing = true;
                        return authAPI.refresh(refreshToken)
                            .then((data) => {
                                if (data && data.success && data.data && data.data.access_token) {
                                    const newToken = data.data.access_token;
                                    const newRefresh = data.data.refresh_token || refreshToken;
                                    localStorage.setItem('token', newToken);
                                    localStorage.setItem('authToken', newToken);
                                    localStorage.setItem('refresh_token', newRefresh);
                                    processQueue(null, newToken);
                                    originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                                    return httpClient(originalRequest);
                                }
                                const err = new Error('Token refresh failed');
                                processQueue(err, null);
                                throw err;
                            })
                            .catch((refreshErr) => {
                                processQueue(refreshErr, null);
                                clearSession();
                                return Promise.reject(refreshErr);
                            })
                            .finally(() => {
                                isRefreshing = false;
                            });
                    }
                }
            }

            // Handle unified error response format from enhanced backend
            if (data && typeof data === 'object' && data.success === false) {
                const enhancedError = new Error(data.error?.message || 'API Error');
                enhancedError.code = data.error?.code;
                enhancedError.details = data.error?.details;
                enhancedError.requestId = requestId || data.error?.request_id;
                enhancedError.status = status;
                enhancedError.level = data.error?.level;
                
                return Promise.reject(enhancedError);
            }
            
            // Handle legacy error format
            const enhancedError = new Error(data?.message || error.message || 'API Error');
            enhancedError.status = status;
            enhancedError.requestId = requestId;
            enhancedError.response = error.response;
            
            return Promise.reject(enhancedError);
        }
        
        // Network or other errors
        console.log('[HttpClient] Network error - no response received');
        return Promise.reject(error);
    }
);

// Workflow API methods that connect to our Phase 2 backend
export const workflowAPI = {
    // Execute workflow - connects to apps/workflows/tasks/workflow_task_manager.py
    executeWorkflow: async (workflowData) => {
        const response = await httpClient.post('/api/workflows/execute/', workflowData);
        return response.data;
    },

    // Shared monthly Claude credit - connects to apps/workflows/views.py get_claude_budget
    getClaudeBudget: async () => {
        const response = await httpClient.get('/api/workflows/claude-budget/');
        return response.data;
    },
    
    // Get workflow status - connects to workflow status tracking
    getWorkflowStatus: async (jobId) => {
        const response = await httpClient.get(`/api/workflows/jobs/${jobId}/status/`);
        return response.data;
    },

    // Get workflow results (presigned download URLs)
    getWorkflowResults: async (jobId) => {
        const response = await httpClient.get(`/api/workflows/jobs/${jobId}/results/`);
        return response.data;
    },

    // List workflow jobs with optional filters
    listWorkflowJobs: async (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.days) queryParams.append('days', params.days);
        if (params.status) queryParams.append('status', params.status);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.offset) queryParams.append('offset', params.offset);
        const queryString = queryParams.toString();
        const url = queryString ? `/api/workflows/jobs/?${queryString}` : '/api/workflows/jobs/';
        const response = await httpClient.get(url);
        return response.data;
    },

    // Update workflow name
    updateWorkflowName: async (jobId, newName) => {
        const response = await httpClient.patch(`/api/workflows/jobs/${jobId}/name/`, { name: newName });
        return response.data;
    }
};

// Cloud storage API methods - connects to apps/cloud_storage/views.py
export const cloudAPI = {
    // List cloud items
    listItems: async (parentId = null, includeOrgItems = true, onlyOrg = false) => {
        const params = { include_org_items: includeOrgItems };
        if (onlyOrg) params.only_org = true;
        if (parentId) params.parent_id = parentId;
        const response = await httpClient.get('/api/cloud/files/', { params });
        return response.data;
    },

    // Create folder
    createFolder: async (name, parentId = null, forOrg = false) => {
        const payload = { name };
        if (parentId) payload.parent_id = parentId;
        if (forOrg) payload.for_org = true;
        const response = await httpClient.post('/api/cloud/files/folders/', payload);
        return response.data;
    },

    // Upload file
    uploadFile: async (file, parentId = null, forOrg = false, onProgress = null) => {
        const formData = new FormData();
        formData.append('file', file);
        if (parentId) formData.append('parent_id', parentId);
        formData.append('for_org', forOrg ? 'true' : 'false');
        const response = await httpClient.post('/api/cloud/files/upload/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: onProgress || undefined
        });
        return response.data;
    },

    // Download file (returns AxiosResponse)
    downloadFile: async (itemId) => {
        const response = await httpClient.get(`/api/cloud/files/${itemId}/download/`, { responseType: 'blob' });
        return response;
    },

    // Rename item
    renameItem: async (itemId, newName) => {
        const response = await httpClient.put(`/api/cloud/files/${itemId}/rename/`, { name: newName });
        return response.data;
    },

    // Move item
    moveItem: async (itemId, parentId) => {
        const response = await httpClient.put(`/api/cloud/files/${itemId}/move/`, { parent_id: parentId });
        return response.data;
    },

    // Delete item
    deleteItem: async (itemId) => {
        const response = await httpClient.delete(`/api/cloud/files/${itemId}/delete/`);
        return response.data;
    },

    // Start vectorization
    startVectorization: async (itemId, sourceColumn, targetColumns) => {
        const response = await httpClient.post(`/api/cloud/files/${itemId}/vectorize/`, {
            source_column: sourceColumn,
            target_columns: targetColumns
        });
        return response.data;
    },

    // Vectorization progress
    getVectorizationProgress: async (taskId) => {
        const response = await httpClient.get(`/api/cloud/vectorization/progress/${taskId}/`);
        return response.data;
    },

    // Active vectorizations
    getActiveVectorizations: async () => {
        const response = await httpClient.get('/api/cloud/vectorization/active/');
        return response.data;
    },

    // Clear vectorization status for a file
    clearVectorizationStatus: async (itemId) => {
        const response = await httpClient.post(`/api/cloud/files/${itemId}/vectorization/clear/`);
        return response.data;
    },

    // Vectorization combinations
    getVectorizationCombinations: async (itemId) => {
        const response = await httpClient.get(`/api/cloud/files/${itemId}/combinations/`);
        return response.data;
    },

    createVectorizationCombination: async (itemId, sourceColumn, targetColumns) => {
        const response = await httpClient.post(`/api/cloud/files/${itemId}/combinations/create/`, {
            source_column: sourceColumn,
            target_columns: targetColumns
        });
        return response.data;
    },

    getVectorizationCombinationDetails: async (itemId, comboId) => {
        const response = await httpClient.get(`/api/cloud/files/${itemId}/combinations/${comboId}/`);
        return response.data;
    },

    deleteVectorizationCombination: async (itemId, comboId) => {
        const response = await httpClient.delete(`/api/cloud/files/${itemId}/combinations/${comboId}/delete/`);
        return response.data;
    },

    setDefaultVectorizationCombination: async (itemId, comboId) => {
        const response = await httpClient.put(`/api/cloud/files/${itemId}/combinations/${comboId}/default/`);
        return response.data;
    },

    // Spreadsheet Editor APIs
    // Get spreadsheet content for editing
    getSpreadsheetContent: async (itemId) => {
        const response = await httpClient.get(`/api/spreadsheet/${itemId}/content/`);
        return response;
    },

    // Save spreadsheet content
    saveSpreadsheet: async (itemId, data) => {
        const response = await httpClient.put(`/api/spreadsheet/${itemId}/save/`, data);
        return response.data;
    },

    // Export spreadsheet to different format
    exportSpreadsheet: async (itemId, data) => {
        const response = await httpClient.post(`/api/spreadsheet/${itemId}/export/`, data, {
            responseType: 'blob'
        });
        return response;
    },

    // Get vectorization status for a file
    getVectorizationStatus: async (itemId) => {
        const response = await httpClient.get(`/api/spreadsheet/${itemId}/vectorization-status/`);
        return response.data;
    },

    // Start incremental vectorization update
    startIncrementalVectorization: async (itemId, combinationId) => {
        const response = await httpClient.post(`/api/spreadsheet/${itemId}/vectorization/update/`, {
            combination_id: combinationId
        });
        return response.data;
    },

    // Get pending vectorization changes count
    getVectorizationChanges: async (itemId, combinationId) => {
        const response = await httpClient.get(`/api/spreadsheet/${itemId}/vectorization/changes/`, {
            params: { combination_id: combinationId }
        });
        return response.data;
    },

    // ==================== Collaboration APIs ====================

    // Create or join a collaboration session
    createCollaborationSession: async (itemId) => {
        const response = await httpClient.post(`/api/spreadsheet/${itemId}/collaboration/session/`);
        return response.data;
    },

    // Get collaboration session info
    getCollaborationSession: async (itemId) => {
        const response = await httpClient.get(`/api/spreadsheet/${itemId}/collaboration/session/info/`);
        return response.data;
    },

    // Leave a collaboration session
    leaveCollaborationSession: async (itemId) => {
        const response = await httpClient.post(`/api/spreadsheet/${itemId}/collaboration/leave/`);
        return response.data;
    },

    // Update presence (heartbeat) and cursor position
    updatePresence: async (itemId, cursorPosition = null) => {
        const response = await httpClient.post(`/api/spreadsheet/${itemId}/collaboration/presence/`, {
            cursor_position: cursorPosition
        });
        return response.data;
    }
};

// Prompts API methods - connects to apps/prompts/enhanced_views.py
export const promptsAPI = {
    // Get enhanced prompts with default templates
    getEnhancedPrompts: async (moduleType = null) => {
        const params = moduleType ? { module_type: moduleType } : {};
        const response = await httpClient.get('/api/prompts/enhanced/', { params });
        return response.data;
    },
    
    // Get default template for specific module type
    getDefaultTemplate: async (moduleType) => {
        const response = await httpClient.get(`/api/prompts/enhanced/default/${moduleType}/`);
        return response.data;
    },
    
    // Validate template (expects template_text)
    validateTemplate: async (templateText, moduleType) => {
        const response = await httpClient.post('/api/prompts/enhanced/validate/', {
            template_text: templateText,
            module_type: moduleType
        });
        return response.data;
    },

    // ===== Legacy prompt CRUD (views_prompt.py) for compatibility =====
    getPrompts: async (params = {}) => {
        const response = await httpClient.get('/api/prompts/', { params });
        return response.data;
    },
    createUserPrompt: async (payload) => {
        const response = await httpClient.post('/api/prompts/user/', payload);
        return response.data;
    },
    createOrgPrompt: async (payload) => {
        const response = await httpClient.post('/api/prompts/org/', payload);
        return response.data;
    },
    updateUserPrompt: async (promptId, payload) => {
        const response = await httpClient.put(`/api/prompts/user/${promptId}/`, payload);
        return response.data;
    },
    updateOrgPrompt: async (promptId, payload) => {
        const response = await httpClient.put(`/api/prompts/org/${promptId}/`, payload);
        return response.data;
    },
    deleteUserPrompt: async (promptId) => {
        const response = await httpClient.delete(`/api/prompts/user/${promptId}/delete/`);
        return response.data;
    },
    deleteOrgPrompt: async (promptId) => {
        const response = await httpClient.delete(`/api/prompts/org/${promptId}/delete/`);
        return response.data;
    }
};

// Error tracking API methods - connects to RAG_translation/views_error_enhanced.py
export const errorAPI = {
    // Get paginated errors
    getErrors: async (filters = {}) => {
        const response = await httpClient.get('/api/errors/paginated/', { params: filters });
        return response.data;
    },
    
    // Get workflow-specific errors
    getWorkflowErrors: async (workflowId) => {
        const response = await httpClient.get(`/api/errors/workflow/${workflowId}/`);
        return response.data;
    },
    
    // Get error trends
    getErrorTrends: async (days = 7) => {
        const response = await httpClient.get('/api/errors/trends/', { params: { days } });
        return response.data;
    }
};

// Authentication API methods - connects to apps/accounts/views.py
export const authAPI = {
    // Login
    login: async (username, password, captchaToken = '') => {
        const body = { username, password };
        if (captchaToken) {
            body.captcha_token = captchaToken;
        }
        const response = await httpClient.post('/api/accounts/login/', body);
        return response.data;
    },
    
    // Register
    register: async (userData) => {
        const response = await httpClient.post('/api/accounts/register/', userData);
        return response.data;
    },

    // CAPTCHA public runtime config
    getCaptchaConfig: async () => {
        const response = await httpClient.get('/api/accounts/captcha-config/');
        return response.data;
    },
    
    // Verify email - using GET with token parameter as per backend implementation
    verifyEmail: async (token) => {
        const response = await httpClient.get(`/api/accounts/verify-email/?token=${token}`);
        return response.data;
    },

    // Token management additions
    refresh: async (refreshToken) => {
        const response = await httpClient.post('/api/accounts/token/refresh/', { refresh_token: refreshToken });
        return response.data;
    },
    validate: async (token) => {
        const response = await httpClient.post('/api/accounts/token/validate/', { token });
        return response.data;
    },
    logout: async (token) => {
        const response = await httpClient.post('/api/accounts/logout/', { token });
        return response.data;
    },

    // Invitation code (admin-only)
    requestInvitationCode: async ({ admin_username, admin_password, email }) => {
        const response = await httpClient.post('/api/accounts/invitation-code/', {
            admin_username,
            admin_password,
            email,
        });
        return response.data;
    },

    // List active invitations (admin only)
    getActiveInvitations: async (params = {}) => {
        const response = await httpClient.get('/api/accounts/invitations/active/', { params });
        return response.data;
    },

    // Invitation stats (admin only)
    getInvitationStats: async (params = {}) => {
        const response = await httpClient.get('/api/accounts/invitations/stats/', { params });
        return response.data;
    },

    // Verification code (CAPTCHA) endpoints (legacy-compatible)
    getVerificationCode: async () => {
        console.log('[AuthAPI] Getting verification code from:', httpClient.defaults.baseURL + '/api/accounts/verify-code/');
        const response = await httpClient.get('/api/accounts/verify-code/');
        // Persist session id for follow-up verification in cross-site setups
        try {
            if (response?.data?.session_id) {
                localStorage.setItem('verifyCodeSessionId', response.data.session_id);
            }
        } catch (_) { /* ignore */ }
        return response.data;
    },

    checkVerificationCode: async (payload) => {
        // Auto-attach session_id from localStorage if not provided
        const body = { ...payload };
        if (body.session_id == null) {
            const sid = localStorage.getItem('verifyCodeSessionId');
            if (sid) body.session_id = sid;
        }
        const response = await httpClient.post('/api/accounts/check-verify-code/', body);
        return response.data;
    }
};

// Harness auth-bridge API (M19.5, INV-M19.5-AGENT-TAB-SENDER-001).
//
// Exchanges the Django access token for a short-lived Translation Harness access
// token via the harness `POST /auth/exchange`. This deliberately does NOT reuse the
// Django `httpClient` instance: that instance targets the Django API base URL and its
// interceptors attach Django X-User-* / Authorization headers. The harness lives on a
// different origin, so we issue a dedicated cross-origin request here.
//
// Leak controls (sender half of INV-M19.5-LEAK-001): the Django token is sent in the
// Authorization header, NEVER in the URL/query string.
const resolveHarnessApiBaseURL = () => {
    try {
        if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_HARNESS_API_BASE_URL) {
            return String(process.env.REACT_APP_HARNESS_API_BASE_URL).trim();
        }
    } catch (_) { /* ignore */ }
    return '';
};

export const harnessAuthAPI = {
    // Returns the harness token payload, e.g. { access_token, token_type, expires_in, expires_at? }.
    exchange: async (djangoAccessToken) => {
        if (!djangoAccessToken) {
            throw new Error('Missing Django access token for harness exchange');
        }
        const base = resolveHarnessApiBaseURL();
        const url = `${base}/auth/exchange`;
        const response = await axios.post(url, {}, {
            headers: {
                Authorization: `Bearer ${djangoAccessToken}`,
                'Content-Type': 'application/json',
            },
            // The harness exchange is authenticated by the bearer token, not by cookies.
            withCredentials: false,
        });
        return response.data;
    }
};

// User Profile API methods - connects to apps/accounts/views.py
export const userAPI = {
    // Get user profile summary including basic info, file stats, cost info, and recent workflows
    getUserProfile: async () => {
        const response = await httpClient.get('/api/accounts/profile-summary/');
        return response.data;
    }
};

// Version Update API methods - connects to apps/accounts/views.py
export const versionAPI = {
    // Check if user needs to see a version update notification
    checkUpdate: async () => {
        const response = await httpClient.get('/api/accounts/version/check/');
        return response.data;
    },

    // Get latest version information
    getLatest: async () => {
        const response = await httpClient.get('/api/accounts/version/latest/');
        return response.data;
    },

    // Acknowledge version has been seen by user
    acknowledge: async (version) => {
        const response = await httpClient.post('/api/accounts/version/acknowledge/', { version });
        return response.data;
    },

    // Get version history list
    getHistory: async (limit = 10) => {
        const response = await httpClient.get('/api/accounts/version/history/', { params: { limit } });
        return response.data;
    }
};

// Legacy API has been removed - use the appropriate modern APIs:
// - File uploads: use cloudAPI.uploadFile()
// - File downloads: use cloudAPI or workflow result endpoints
// - Job results: use workflowAPI.getWorkflowStatus() and access result_files

export default httpClient;
